/*=========================================================================================
  File Name: router.js
==========================================================================================*/

import Vue from 'vue'
import Router from 'vue-router'

import { requireAuth } from '@/utils/auth'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior () {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        {
          path: '/',
          redirect: '/login'
        },
        {
          path: '/login',
          name: 'Login',
          component: () => import('./views/pages/Login')
        },
        {
          path: '/mfa',
          name: 'MFA',
          component: () => import('./views/pages/MFA')
        },
        {
          path: '/expedited-shipping',
          name: 'ShippingCalculator',
          component: () => import('./views/pages/ShippingCalculator')
        },
        {
          path: '/new-password',
          name: 'NewPassword',
          component: () => import('./views/pages/NewPassword')
        },
        {
          path: '/mobile-yearbooks',
          name: 'MobileYearbooks',
          component: () => import('./views/pages/MobileYearbooks')
        }
      ]
    },
    {
      path: '/admin',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        {
          path: '/admin/profile',
          name: 'Profile',
          component: () => import('./views/pages/admin/my-account/Profile'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Profile', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Profile', active: true }
            ],
            pageTitle: 'Profile'
          }
        },
        {
          path: '/admin/settings',
          name: 'Settings',
          component: () => import('./views/pages/admin/my-account/Settings'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Settings', active: true }
            ],
            pageTitle: 'Settings'
          }
        },
        {
          path: '/admin/submission_status',
          name: 'SubmissionStatus',
          component: () => import('./views/pages/admin/my-account/SubmissionStatus'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Submission Status', active: true }
            ],
            pageTitle: 'Submission Status'
          }
        },
        {
          path: '/admin/marketing_import',
          name: 'MarketingImport',
          component: () => import('./views/pages/admin/my-account/marketing/MarketingImport'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Import Marketing Contacts', active: true }
            ],
            pageTitle: 'Import Marketing Contacts'
          }
        },
        {
          path: '/admin/my-account',
          name: 'MyAccount',
          component: () => import('./views/pages/admin/my-account/MyAccount'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'My Account', active: true }
            ],
            pageTitle: 'My Account'
          }
        },
        {
          path: '/admin/ypa',
          name: 'SchoolSignUp',
          component: () => import('./views/pages/admin/YPA.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'School Sign-up', active: true }
            ],
            pageTitle: 'School Sign-up'
          }
        },
        {
          path: '/admin/accounts',
          name: 'Accounts',
          component: () => import('./views/pages/admin/accounts/AccountList'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Accounts', active: true }
            ],
            pageTitle: 'Accounts'
          }
        },
        {
          path: '/admin/users',
          name: 'Users',
          component: () => import('./views/pages/admin/users/UserList'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Users', active: true }
            ],
            pageTitle: 'Users'
          }
        },
        {
          path: '/admin/projects',
          name: 'Projects',
          component: () => import('./views/pages/admin/projects/ProjectList'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Projects', active: true }
            ],
            pageTitle: 'Project Records'
          }
        },
        {
          path: '/admin/online-sales',
          name: 'OnlineSales',
          component: () => import('./views/pages/admin/online-sales/OnlineSales'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Online Sales', active: true }
            ],
            pageTitle: 'Online Sales'
          }
        },
        {
          path: '/admin/online-sales/orders',
          name: 'OnlineOrderList',
          component: () => import('./views/pages/admin/online-sales/OnlineOrderList'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Online Sales', url: null },
              { title: 'Orders', active: true }
            ],
            pageTitle: 'Online Orders'
          }
        },
        {
          path: '/admin/sales-store-coupons',
          name: 'DiscountList',
          component: () => import('./views/pages/admin/discounts/DiscountList'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Store Coupons', active: true }
            ],
            pageTitle: 'Store Coupons'
          }
        },
        {
          path: '/admin/marketing-campaigns',
          name: 'MarketingCampaignList',
          component: () => import('./views/pages/admin/marketing/MarketingCampaignList'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Marketing Campaigns', active: true }
            ],
            pageTitle: 'Marketing Campaigns'
          }
        },
        {
          path: '/admin/request-bulk-presale-funds',
          name: 'FundRequestList',
          component: () => import('./views/pages/admin/funds/FundRequestList'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Bulk Presale Fund Request', active: true }
            ],
            pageTitle: 'Bulk Presale Fund Request'
          }
        },
        {
          path: '/admin/request-sales-tax',
          name: 'SalesTaxRequestList',
          component: () => import('./views/pages/admin/funds/SalesTaxRequestList'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Sales Tax Request', active: true }
            ],
            pageTitle: 'Sales Tax Request'
          }
        },
        {
          path: '/admin/request-single-book-funds',
          name: 'SummerFundRequestList',
          component: () => import('./views/pages/admin/funds/SummerFundRequestList'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Single Book Fund Request', active: true }
            ],
            pageTitle: 'Single Book Fund Request'
          }
        },
        {
          path: '/admin/proofs',
          name: 'Proofs',
          component: () => import('./views/pages/admin/proofs/ProofList'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Proofs', active: true }
            ],
            pageTitle: 'Proofs'
          }
        },
        {
          path: '/admin/proofs/:id',
          name: 'Proof',
          component: () => import('./views/pages/admin/proofs/Proof'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Proofs', url: '/admin/proofs' },
              { title: 'Proof', active: true }
            ],
            parent: 'proofs',
            pageTitle: 'Proof'
          }
        },
        {
          path: '/admin/upload',
          name: 'upload',
          component: () => import('./views/pages/admin/Upload.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Upload Proof', active: true }
            ],
            pageTitle: 'Upload Proof'
          }
        },
        {
          path: '/admin/orders',
          name: 'orders',
          component: () => import('./views/pages/admin/orders/OrderList'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Admin', url: null },
              { title: 'Orders', active: true }
            ],
            pageTitle: 'Order Status'
          }
        },
        {
          path: '/admin/super/order-admin',
          name: 'OrderAdmin',
          component: () => import('./views/pages/admin/super/order-admin/OrderAdminList.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/my-account' },
              { title: 'Admin', url: null },
              { title: 'Super', url: null },
              { title: 'Order Admin', active: true }
            ],
            pageTitle: 'Order Admin'
          }
        },
        {
          path: '/admin/super/personalization-upload',
          name: 'PersonalizationUpload',
          component: () => import('./views/pages/admin/super/PersonalizationUpload.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/my-account' },
              { title: 'Admin', url: null },
              { title: 'Super', url: null },
              { title: 'Personalization Upload', active: true }
            ],
            pageTitle: 'Personalization Upload'
          }
        },
        {
          path: '/admin/super/customer-pricing',
          name: 'CustomerPricing',
          component: () => import('./views/pages/admin/super/customer-pricing/CustomerPricingList.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/my-account' },
              { title: 'Admin', url: null },
              { title: 'Super', url: null },
              { title: 'Customer Pricing', active: true }
            ],
            pageTitle: 'Customer Pricing'
          }
        },
        {
          path: '/admin/super/pricing-adjustments',
          name: 'PricingAdjustments',
          component: () => import('./views/pages/admin/super/pricing-adjustments/PricingAdjustmentList.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/my-account' },
              { title: 'Admin', url: null },
              { title: 'Super', url: null },
              { title: 'Pricing Adjustments', active: true }
            ],
            pageTitle: 'Pricing Adjustments'
          }
        },
        {
          path: '/admin/manage-credit-cards',
          name: 'StripeCards',
          component: () => import('./views/pages/admin/super/stripe/StripeCardList.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/my-account' },
              { title: 'Admin', url: null },
              { title: 'Manage Credit Cards', active: true }
            ],
            pageTitle: 'Manage Credit Cards'
          }
        },
        {
          path: '/admin/super/processing/proofs',
          name: 'ProcessingProofs',
          component: () => import('./views/pages/admin/super/processing/proofs/ProofProcessingList.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/my-account' },
              { title: 'Admin', url: null },
              { title: 'Super', url: null },
              { title: 'Processing', url: null },
              { title: 'Proofs', active: true }
            ],
            pageTitle: 'Proof Queue'
          }
        },
        {
          path: '/admin/super/processing/uploads',
          name: 'ProcessingUploads',
          component: () => import('./views/pages/admin/super/processing/uploads/UploadList.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/my-account' },
              { title: 'Admin', url: null },
              { title: 'Super', url: null },
              { title: 'Processing', url: null },
              { title: 'Uploads', active: true }
            ],
            pageTitle: 'Upload Queue'
          }
        },
        {
          path: '/admin/super/processing/notifications',
          name: 'ProcessingNotifications',
          component: () => import('./views/pages/admin/super/processing/notifications/NotificationList.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/my-account' },
              { title: 'Admin', url: null },
              { title: 'Super', url: null },
              { title: 'Processing', url: null },
              { title: 'Notifications', active: true }
            ],
            pageTitle: 'Notification Queue'
          }
        },
        {
          path: '/admin/super/processing/order-moves',
          name: 'ProcessingOrderMoves',
          component: () => import('./views/pages/admin/super/processing/orders/OrderMoveList.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/my-account' },
              { title: 'Admin', url: null },
              { title: 'Super', url: null },
              { title: 'Processing', url: null },
              { title: 'Order Moves', active: true }
            ],
            pageTitle: 'Order Move Queue'
          }
        },
        {
          path: '/admin/super/processing/epubs',
          name: 'ProcessingEpubs',
          component: () => import('./views/pages/admin/super/processing/epubs/EpubList.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/my-account' },
              { title: 'Admin', url: null },
              { title: 'Super', url: null },
              { title: 'Processing', url: null },
              { title: 'EPUBs', active: true }
            ],
            pageTitle: 'EPUB Queue'
          }
        },
        {
          path: '/admin/super/invoices',
          name: 'Invoices',
          component: () => import('./views/pages/admin/super/invoices/InvoiceList.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/my-account' },
              { title: 'Admin', url: null },
              { title: 'Super', url: null },
              { title: 'Charge CC', active: true }
            ],
            pageTitle: 'Charge CC'
          }
        },
        {
          path: '/admin/super/quickbooks-invoices',
          name: 'Quickbooks Invoices',
          component: () => import('./views/pages/admin/super/quickbooks-invoices/QuickbooksInvoiceList.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/my-account' },
              { title: 'Admin', url: null },
              { title: 'Super', url: null },
              { title: 'Quickbooks Invoices', active: true }
            ],
            pageTitle: 'Quickbooks Invoices'
          }
        },
        {
          path: '/admin/super/processing/processor-statuses',
          name: 'ProcessorStatuses',
          component: () => import('./views/pages/admin/super/processing/ProcessorStatuses.vue'),
          beforeEnter: requireAuth,
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/my-account' },
              { title: 'Admin', url: null },
              { title: 'Super', url: null },
              { title: 'Processing', url: null },
              { title: 'Processor Statuses', active: true }
            ],
            pageTitle: 'Processor Statuses'
          }
        },
        {
          path: '/admin/*',
          redirect: '/admin/profile'
        },
        {
          path: '',
          redirect: '/admin/profile'
        }
      ]
    },
    {
      path: '*',
      redirect: '/login'
    }
  ]
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = "none";
  }
})

export default router
